import { createVNode, render } from 'vue'
import AppDialog from '@/components/feedback/AppDialog.vue'

interface DialogOptions {
  title?: string
  message?: string
  confirmButtonText?: string
  cancelButtonText?: string
  showCancelButton?: boolean
  confirmButtonColor?: string
  cancelButtonColor?: string
  closeOnClickOverlay?: boolean
  beforeClose?: (action: string) => boolean | Promise<boolean>
}

interface DialogInstance {
  show: (options: DialogOptions) => Promise<'confirm' | 'cancel'>
  confirm: (options: Omit<DialogOptions, 'showCancelButton'>) => Promise<boolean>
  alert: (message: string, options?: Omit<DialogOptions, 'showCancelButton' | 'message'>) => Promise<boolean>
  hide: () => void
}

class DialogManager {
  private container: HTMLElement | null = null
  private vnode: any = null
  private currentResolve: ((value: 'confirm' | 'cancel') => void) | null = null
  // 保存应用上下文，用于将 Vant 安装到 vnode 上下文
  public appContext: any = null

  private createContainer() {
    if (!this.container) {
      this.container = document.createElement('div')
      this.container.className = 'app-dialog-container'
      this.container.style.position = 'fixed'
      this.container.style.top = '0'
      this.container.style.left = '0'
      this.container.style.width = '100%'
      this.container.style.height = '100%'
      this.container.style.zIndex = '9999'
      this.container.style.pointerEvents = 'auto'
      document.body.appendChild(this.container)
    }
    return this.container
  }

  private createVNode(props: any) {
    const vnode = createVNode(AppDialog, {
      ...props,
      'onUpdate:show': (visible: boolean) => {
        if (!visible && this.currentResolve) {
          this.currentResolve('cancel')
          this.currentResolve = null
        }
      },
      onConfirm: () => {
        if (this.currentResolve) {
          this.currentResolve('confirm')
          this.currentResolve = null
        }
        this.cleanUp()
      },
      onCancel: () => {
        if (this.currentResolve) {
          this.currentResolve('cancel')
          this.currentResolve = null
        }
        this.cleanUp()
      }
    })
    // 关键：把应用上下文传给 vnode，确保能使用 Vant 组件
    if (this.appContext) {
      vnode.appContext = this.appContext
    }
    return vnode
  }

  show(options: DialogOptions): Promise<'confirm' | 'cancel'> {
    // 确保清理之前的状态
    this.hide()
    
    const container = this.createContainer()
    
    return new Promise((resolve) => {
      this.currentResolve = resolve

      const props = {
        show: true,
        title: options.title || '',
        message: options.message || '',
        showCancelButton: options.showCancelButton !== false,
        confirmButtonText: options.confirmButtonText || 'Confirm',
        cancelButtonText: options.cancelButtonText || 'Cancel',
        confirmButtonColor: options.confirmButtonColor,
        cancelButtonColor: options.cancelButtonColor,
        closeOnClickOverlay: options.closeOnClickOverlay || false,
        beforeClose: options.beforeClose
      }

      this.vnode = this.createVNode(props)
      render(this.vnode, container)
    })
  }

  async confirm(options: Omit<DialogOptions, 'showCancelButton'>): Promise<boolean> {
    const result = await this.show({
      ...options,
      showCancelButton: true
    })
    return result === 'confirm'
  }

  async alert(message: string, options: Omit<DialogOptions, 'showCancelButton' | 'message'> = {}): Promise<boolean> {
    const result = await this.show({
      ...options,
      message,
      showCancelButton: false
    })
    return result === 'confirm'
  }

  private cleanUp() {
    if (this.vnode && this.container) {
      render(null, this.container)
      this.vnode = null
    }
    // 移除容器
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }

  hide() {
    this.cleanUp()
    // 重置状态，但不调用 resolve (让事件处理器处理)
    this.currentResolve = null
  }

  destroy() {
    this.hide()
  }
}

// 创建全局实例
const dialogManager = new DialogManager()

// Composable hook
export function useDialog(): DialogInstance {
  return {
    show: (options) => dialogManager.show(options),
    confirm: (options) => dialogManager.confirm(options),
    alert: (message, options) => dialogManager.alert(message, options),
    hide: () => dialogManager.hide()
  }
}

// 全局方法（可选）
export const $dialog = {
  show: (options: DialogOptions) => dialogManager.show(options),
  confirm: (options: Omit<DialogOptions, 'showCancelButton'>) => dialogManager.confirm(options),
  alert: (message: string, options?: Omit<DialogOptions, 'showCancelButton' | 'message'>) => 
    dialogManager.alert(message, options),
  hide: () => dialogManager.hide()
}

// Vue 插件安装方法
export default {
  install(app: any) {
    // 记录应用上下文，供动态 vnode 使用
    dialogManager.appContext = app._context
    app.config.globalProperties.$dialog = $dialog
    app.provide('dialog', $dialog)
  }
}
